import { getQueryArgs, addQueryArgs } from '../utils';
import CopyButton from './copy-button';


/**
 * Updates the specified link element's href with a new value for the specified
 * query arg
 * @param  {Element} el     The link element
 * @param  {Object}  params Object of URL params in key:value pairs
 * @return {void}
 */
const updateLinkHref = ( el, params ) => {
    const baseUrl = el.href.replace( el.search, '' );

    let args = getQueryArgs( el.href );
    for( let [k, v] of Object.entries( params ) ) {
        args[k] = v;
    }

    el.href = addQueryArgs( baseUrl, args );
};


/**
 * Returns true if the button is of the specified type
 * @param  {Element} btn  The button element
 * @param  {String}  type The button type to check for
 * @return {void}
 */
const isButton = ( btn, type ) => {
    return btn.classList.contains( `sharing-menu__button--${type}` );
};


/**
 *
 */
export default class InlineShare {

    /**
     * Creates a new instance of the inline sharing menu functionaltiy
     * @param {Element} el The parent container element to watch for select
     *                     events on
     */
    constructor( el ) {
        this.el = el;

        this._initMenu();

        // Toggle the menu when the mouse is released
        this.el.addEventListener( 'mouseup', e => {
            this._onSelect( e );
        } );

        // Toggle the menu when keyboard selection ends
        document.addEventListener( 'keyup', e => {
            this._onSelect( e );
        } );

        // Hide the menu when outside clicks occur
        document.addEventListener( 'click', e => {
            this._onOutsideClick( e );
        } );
    }

    /**
     * @param  {Event} e The event object
     * @return {void}
     */
    _onSelect() {
        const selection = window.getSelection();

        if (
            selection.toString().length <= 0 ||
            ! this.el.contains( selection.anchorNode )
        ) {
            this.hide();
            return;
        }

        // Get the position of the selection
        const bounds = selection.getRangeAt( 0 ).getBoundingClientRect();
        const top    = bounds.top + window.scrollY;
        const left   = bounds.left + bounds.width/2 + window.scrollX;

        // Update the button actions with the new selection
        this.menu._buttons.forEach( btn => {
            this._updateButton( btn, selection.toString() );
        } );

        // Display the menu
        this.show( { top, left } );
    }

    /**
     * Hides the menu when clicks occur outside of the Inline Share UI elements
     * @param  {Event} e The click event object
     * @return {void}
     */
    _onOutsideClick( e ) {
        const selected = window.getSelection().toString();

        // If it's not inside either the container OR the menu => hide
        if ( ! ( this.el.contains( e.target ) || this.menu.contains( e.target ) ) ) {
            this.hide();

        // If it's inside the container AND there's nothing selected => hide
        } else if ( this.el.contains( e.target ) && selected.length === 0 ) {
            this.hide();
        }

    }

    /**
     * Initializes a new instance of the sharing menu from the global menu
     * template
     * @return {Element} The menu instance element
     */
    _initMenu() {
        this.menu = document.getElementById( 'roar-inline-share' );

        if ( ! this.menu ) {
            const template = document.getElementById( 'roar-inline-share-template' );
            const temp = document.createElement( 'div' );
            temp.innerHTML = template.innerHTML.trim();

            this.menu = temp.firstChild;
            this.menu.style.display = 'none';
            this.menu.style.position = "absolute"
            this.menu.style.transform = 'translate(-50%, -100%)'

            this.menu._buttons = this.menu.querySelectorAll( '.sharing-menu__button' );

            // Initialize individual button actions
            this.menu._buttons.forEach( btn => {
                if ( isButton( btn, 'copy' ) ) {
                    btn._copy = new CopyButton( btn );
                }
            } );

            document.body.appendChild( this.menu );
        }
    }

    /**
     * Updates the button actions based on the current selection
     * @param  {Element} btn  The button element to update
     * @param  {String}  text The new selection text
     * @return {void}
     */
    _updateButton( btn, text ) {
        if ( isButton( btn, 'copy' ) ) {
            btn._copy.setText( text );

        } else if ( isButton( btn, 'twitter' ) ) {
            updateLinkHref( btn, { text } );

        } else if ( isButton( btn, 'linkedin' ) ) {
            updateLinkHref( btn, {
                title: text,
                summary: ''
            } );

        } else if ( isButton( btn, 'facebook' ) ) {
            updateLinkHref( btn, { caption: text } );

        } else if ( isButton( btn, 'email' ) ) {
            const baseUrl = window.location.origin + window.location.pathname;
            btn.href = baseUrl + btn.href.replace( /^mailto:/, '' );
            updateLinkHref( btn, { body: `${text}\n${btn.dataset.url}` } );
            btn.href = btn.href.replace( baseUrl, 'mailto:' );
        }
    }


    /**
     * Shows the menu
     * @param  {Object} pos The position (top/left) relative to the document
     *                      where the menu should be displayed
     * @return {void}
     */
    show( pos = { top: 0, left : 0 } ) {
        this.menu.style.top = `${pos.top}px`;
        this.menu.style.left = `${pos.left}px`;
        this.menu.style.display = 'block';
    }

    /**
     * Hides the menu
     * @return {void}
     */
    hide() {
        this.menu.style.top = `0px`;
        this.menu.style.left = `0px`;
        this.menu.style.display = 'none';
    }

}